import { TicketOfficeView } from '../../../types/ticket-office';
import { TicketViewProps } from '../../components/TicketView';
import { getInternalInfoProps } from './InternalInfo';
import { getPosterProps } from './Poster';
import { getTicketSystemProps } from './TicketSystem';
import { getEventDetails } from './EventDetails';
import { getTicketTypesTableProps } from './TicketTypesTable';
import { getCustomerSection } from './CustomerSection';
import { getSeatsByType } from './seat-utils';
import { getFloorPlanProps } from './FloorPlan';

interface AvailabilityDisplay {
    text: string;
    className: string;
}

// Low stock warning thresholds
const LOW_STOCK_ABSOLUTE_THRESHOLD = 10; // Warn when ≤10 tickets remain
const LOW_STOCK_PERCENTAGE_THRESHOLD = 0.1; // Warn when ≤10% of capacity remains

/**
 * Calculate ticket availability display based on backend capacity data.
 *
 * Backend provides:
 * - capacity.total: Physical seat count from floor plan
 * - capacity.available: Remaining bookable seats (total - all bookings - reservations)
 * - capacity.sold: Confirmed sold tickets only
 *
 * Low stock warnings trigger when EITHER:
 * - Absolute threshold: ≤10 seats remaining
 * - Percentage threshold: ≤10% of capacity remaining
 */
const getAvailabilityDisplay = (view: TicketOfficeView): AvailabilityDisplay => {
    const { state } = view;
    const { lang } = state;

    // Get capacity from API response
    const capacity = state.seats.capacity;

    // If no capacity data yet (initial load), show placeholder
    if (!capacity) {
        return {
            text: lang.sections.tickets,
            className: 'k-body-bold block font-semibold mb-2',
        };
    }

    // Backend already accounts for all bookings including current session
    // So available is the actual remaining capacity that can be booked
    const available = capacity.available;
    const total = capacity.total;

    // Check thresholds - warn if EITHER absolute or percentage threshold is met
    const isLowStockAbsolute = available <= LOW_STOCK_ABSOLUTE_THRESHOLD;
    const isLowStockPercentage = total > 0 && available <= total * LOW_STOCK_PERCENTAGE_THRESHOLD;
    const isLowStock = isLowStockAbsolute || isLowStockPercentage;

    const isOversold = available < 0;
    const isSoldOut = available === 0;

    // State 4: Oversold
    if (isOversold) {
        return {
            text: lang.sections.oversold.replace(':count', String(Math.abs(available))),
            className: 'k-body-bold block font-semibold mb-2 text-kinola-red',
        };
    }

    // State 3: Sold out
    if (isSoldOut) {
        return {
            text: lang.sections.soldOut,
            className: 'k-body-bold block font-semibold mb-2 text-neutral-500',
        };
    }

    // State 2: Low stock (orange warning)
    if (isLowStock) {
        return {
            text: lang.sections.ticketsAvailable.replace(':count', String(available)),
            className: 'k-body-bold block font-semibold mb-2 text-kinola-orange',
        };
    }

    // State 1: Normal
    return {
        text: lang.sections.ticketsAvailable.replace(':count', String(available)),
        className: 'k-body-bold block font-semibold mb-2',
    };
};

export const getTicketView = (view: TicketOfficeView): TicketViewProps => {
    const { state } = view;
    const { lang } = state;

    const seatsByType = getSeatsByType(state);

    // Get dynamic availability display based on capacity and selection
    const availabilityDisplay = getAvailabilityDisplay(view);

    // Determine if warning should be shown (when event is oversold)
    const capacity = state.seats.capacity;
    const shouldShowWarning = capacity && capacity.available < 0;

    const ticketViewProps: TicketViewProps = {
        tabMenu: {
            items: [
                {
                    text: lang.tabs.sell,
                    isActive: true,
                },
                {
                    text: lang.tabs.purchases,
                    url: state.urls.viewPurchases,
                },
            ]
        },
        title: state.production.name,
        internalInfo: getInternalInfoProps(view),
        originalTitle: state.production.originalTitle,
        countries: state.production.countries,
        poster: getPosterProps(view),
        ticketSystem: getTicketSystemProps(view),
        eventDetails: getEventDetails(view),
        availableTicketsTitle: availabilityDisplay.text,
        availableTicketsClass: availabilityDisplay.className,
        capacityWarning: shouldShowWarning ? lang.sections.capacityWarning : undefined,
        ticketTypesTable: getTicketTypesTableProps(view, seatsByType),
        seatsTitle: lang.sections.seats,
        customerTitle: lang.sections.customer,
        customerSection: getCustomerSection(view),
    };

    if (state.isProductSaleEnabled) {
        ticketViewProps.tabMenu.items.push({
            text: lang.tabs.products,
            url: state.urls.meals,
        });
    }

    if (state.floorplanData) {
        ticketViewProps.floorPlan = getFloorPlanProps(view);
    }

    return ticketViewProps;
};
